import {
    useCallback,
    useEffect,
    useLayoutEffect,
    useRef,
    useState,
} from 'react'

/** how close to the viewport edge a floating surface is allowed to land */
const VIEWPORT_MARGIN = 16

export type OriginVertical = 'top' | 'center' | 'bottom'
export type OriginHorizontal = 'left' | 'center' | 'right'

export interface PopoverOrigin {
    vertical: OriginVertical
    horizontal: OriginHorizontal
}

export interface AnchoredPositionOptions {
    /** pushes the target away from the anchor along the vertical axis */
    gap?: number
    /** pin under the anchor, shrink to the viewport; ignores vertical origin */
    fitBelowAnchor?: boolean
}

interface AnchoredPosition {
    left: number
    top: number
    maxHeight?: number
    maxWidth?: number
}

const FRACTIONS = {
    top: 0,
    left: 0,
    center: 0.5,
    bottom: 1,
    right: 1,
} as const

/* The visual viewport is what's actually on screen — on iOS it shrinks and
 * shifts when the soft keyboard comes up, while getBoundingClientRect and
 * innerHeight do not. Clamping against it is what keeps a menu anchored to a
 * focused input from landing behind the keyboard. */
const visualViewport = () => {
    const { innerHeight, innerWidth, visualViewport: viewport } = window
    return {
        height: viewport?.height ?? innerHeight,
        offsetLeft: viewport?.offsetLeft ?? 0,
        offsetTop: viewport?.offsetTop ?? 0,
        width: viewport?.width ?? innerWidth,
    }
}

type VisibleViewport = ReturnType<typeof visualViewport>

const clampToViewport = (
    position: number,
    size: number,
    offset: number,
    limit: number,
) =>
    Math.max(
        offset + VIEWPORT_MARGIN,
        Math.min(position, offset + limit - size - VIEWPORT_MARGIN),
    )

// gives up height to the keyboard instead of being clamped up over its input
const fitBelow = (
    anchor: DOMRect,
    viewport: VisibleViewport,
    left: number,
    width: number,
    gap: number,
): AnchoredPosition => {
    const top = Math.max(
        anchor.bottom + gap,
        viewport.offsetTop + VIEWPORT_MARGIN,
    )
    const maxWidth = Math.max(0, viewport.width - 2 * VIEWPORT_MARGIN)
    return {
        left: clampToViewport(
            left,
            Math.min(width, maxWidth),
            viewport.offsetLeft,
            viewport.width,
        ),
        top,
        maxHeight: Math.max(
            0,
            viewport.offsetTop + viewport.height - VIEWPORT_MARGIN - top,
        ),
        maxWidth,
    }
}

/**
 * Pins `targetRef` to `anchorEl`: the point of the anchor named by
 * `anchorOrigin` meets the point of the target named by `transformOrigin`,
 * clamped inside the visible viewport. `options.gap` clears the anchor
 * vertically (a tooltip); `options.fitBelowAnchor` shrinks under it instead.
 *
 * Returns no position until the target has been measured, so callers hide it
 * for that first (unpainted) layout pass.
 */
export const useAnchoredPosition = (
    anchorEl: HTMLElement | null,
    active: boolean,
    anchorOrigin: PopoverOrigin,
    transformOrigin: PopoverOrigin,
    options: AnchoredPositionOptions = {},
) => {
    const { gap = 0, fitBelowAnchor = false } = options
    const targetRef = useRef<HTMLDivElement>(null)
    const [position, setPosition] = useState<AnchoredPosition>()

    const { vertical: anchorVertical, horizontal: anchorHorizontal } =
        anchorOrigin
    const { vertical: transformVertical, horizontal: transformHorizontal } =
        transformOrigin

    const reposition = useCallback(() => {
        const target = targetRef.current
        if (!anchorEl || !target) return

        const anchor = anchorEl.getBoundingClientRect()
        // the resting size, before an enter transform has scaled it
        const { offsetHeight, offsetWidth } = target
        const viewport = visualViewport()
        const left =
            anchor.left +
            FRACTIONS[anchorHorizontal] * anchor.width -
            FRACTIONS[transformHorizontal] * offsetWidth

        if (fitBelowAnchor) {
            setPosition(fitBelow(anchor, viewport, left, offsetWidth, gap))
            return
        }

        const gapDirection = transformVertical === 'top' ? 1 : -1
        const top =
            anchor.top +
            FRACTIONS[anchorVertical] * anchor.height -
            FRACTIONS[transformVertical] * offsetHeight +
            gap * gapDirection
        setPosition({
            left: clampToViewport(
                left,
                offsetWidth,
                viewport.offsetLeft,
                viewport.width,
            ),
            top: clampToViewport(
                top,
                offsetHeight,
                viewport.offsetTop,
                viewport.height,
            ),
        })
    }, [
        anchorEl,
        anchorHorizontal,
        anchorVertical,
        fitBelowAnchor,
        gap,
        transformHorizontal,
        transformVertical,
    ])

    useLayoutEffect(() => {
        if (!active) {
            setPosition(undefined)
            return
        }
        reposition()
    }, [active, reposition])

    useEffect(() => {
        if (!active) return
        // nothing reflows the target against a moving anchor for us, and a
        // backdrop doesn't stop the page behind it from scrolling
        window.addEventListener('scroll', reposition, true)
        window.addEventListener('resize', reposition)
        window.visualViewport?.addEventListener('resize', reposition)
        window.visualViewport?.addEventListener('scroll', reposition)
        return () => {
            window.removeEventListener('scroll', reposition, true)
            window.removeEventListener('resize', reposition)
            window.visualViewport?.removeEventListener('resize', reposition)
            window.visualViewport?.removeEventListener('scroll', reposition)
        }
    }, [active, reposition])

    return { position, targetRef }
}
